import React, { useState, useEffect } from 'react';
import {
  BarChart2, Search, Zap, GraduationCap, ShieldCheck, Calculator,
  GitMerge, Palette, Scale, Coins, Cross, Bot, Wrench,
  Building2, HeartPulse, Link2, Users, Star, Monitor,
  TrendingUp, Clock, Target, ChevronRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import ValueStreamModal, { hasValueStreamUrlState } from './components/valueStream/ValueStreamModal';

// ─── Icon helper ───────────────────────────────────────────────────────────────
const Icon = ({ name, size = 22, color = 'currentColor', strokeWidth = 1.5 }) => {
  const icons = {
    BarChart2, Search, Zap, GraduationCap, ShieldCheck, Calculator,
    GitMerge, Palette, Scale, Coins, Cross, Bot, Wrench,
    Building2, HeartPulse, Link2, Users, Star, Monitor,
    TrendingUp, Clock, Target, ChevronRight,
  };
  const LucideIcon = icons[name];
  if (!LucideIcon) return null;
  return <LucideIcon size={size} color={color} strokeWidth={strokeWidth} />;
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const PROJECTS = [
  {
    id: 'sec',
    tag: 'Finance · AI',
    title: 'SEC Analysis',
    description: 'Live SEC EDGAR data for any public company. Analyze cash flow, earnings quality, and investment signals from 10-K filings.',
    status: 'Live',
    url: '#sec-tool',
    gradient: 'from-violet-600 to-indigo-600',
    accent: '#818cf8',
    icon: 'BarChart2',
    tech: ['OpenAI', 'SEC EDGAR', 'React'],
  },
  {
    id: 'allergen',
    tag: 'Consumer · AI',
    title: 'Food Allergen Scanner',
    description: 'Instant AI-powered food allergen detection. Scan any ingredient list and get real-time allergen alerts to keep your family safe.',
    status: 'Live',
    url: 'https://food-allergen-scanner-v2w3.vercel.app',
    gradient: 'from-orange-500 to-rose-500',
    accent: '#fb923c',
    icon: 'Search',
    tech: ['Computer Vision', 'OpenAI', 'Vercel'],
  },
  {
    id: 'dink',
    tag: 'Pickleball · Social',
    title: 'Dink',
    description: 'The social app for pickleball players. Find courts, connect with players at your level, organize games, and track your improvement.',
    status: 'Building',
    url: 'https://atdink.com',
    gradient: 'from-cyan-500 to-sky-600',
    accent: '#22d3ee',
    icon: 'Zap',
    tech: ['React', 'Maps API', 'Social Graph'],
  },
  {
    id: 'aiacademy',
    tag: 'Education · AI',
    title: 'AI Academy',
    description: 'Democratizing AI education for the next generation. Hands-on courses and labs teaching practical AI skills to students and professionals.',
    status: 'Building',
    url: '#',
    gradient: 'from-fuchsia-500 to-pink-600',
    accent: '#e879f9',
    icon: 'GraduationCap',
    tech: ['LLMs', 'Curriculum Design', 'EdTech'],
  },
  {
    id: 'eatsafe',
    tag: 'Health · Safety',
    title: 'EatSafe',
    description: 'Restaurant safety intelligence platform. AI analyzes health inspections, reviews, and real-time signals to help you eat with confidence.',
    status: 'Building',
    url: '#',
    gradient: 'from-lime-500 to-green-600',
    accent: '#a3e635',
    icon: 'ShieldCheck',
    tech: ['NLP', 'Data Pipeline', 'Maps API'],
  },
  {
    id: 'tipcalc',
    tag: 'Utility · Finance',
    title: 'Tip Calculator',
    description: 'Sleek, instant tip calculator with bill splitting. Set any tip %, split among any number of people, and get per-person totals in real time.',
    status: 'Live',
    url: '#tip-tool',
    gradient: 'from-yellow-500 to-orange-500',
    accent: '#fbbf24',
    icon: 'Calculator',
    tech: ['React', 'CSS Animations', 'UX Design'],
  },
  {
    id: 'valuestream',
    tag: 'Strategy · AI',
    title: 'AI Value Stream Generator',
    description: 'Map your enterprise value streams and identify where AI can deliver the highest ROI. Input your domain and company context — get a visual roadmap of AI optimization opportunities.',
    status: 'Live',
    url: '#',
    gradient: 'from-violet-600 to-purple-700',
    accent: '#a78bfa',
    icon: 'GitMerge',
    tech: ['AI Research', 'Strategy Framework', 'React'],
  },
  {
    id: 'raahe',
    tag: 'Kids · Artisan',
    title: 'The RaaHe Studio',
    description: 'A kids artisan shop — made by kids, for kids. Handcrafted goods that spark creativity and celebrate young makers.',
    status: 'Live',
    url: 'https://www.theraahe.com',
    gradient: 'from-purple-500 to-violet-600',
    accent: '#7c3aed',
    icon: 'Palette',
    tech: ['Square Online', 'E-commerce', 'Handcrafted'],
  },
];

// ─── Prompt Library Data ───────────────────────────────────────────────────────
const PROMPTS = [
  {
    id: 'ghibli',
    category: 'Creative & Image',
    categoryIcon: '',
    title: 'Studio Ghibli Style Transfer',
    description: 'Transform any photo into a stunning Studio Ghibli watercolor painting. Works with portraits, landscapes, and family photos.',
    prompt: `Restyle this image in Studio Ghibli animation style. Keep all facial features, composition, and details intact — only change the artistic style to match Miyazaki's signature hand-painted watercolor aesthetic. Use soft color palettes, lush backgrounds, and that characteristic warm, dreamlike quality.`,
    tools: ['Midjourney', 'DALL-E 3', 'Stable Diffusion'],
    exampleOutput: 'Photo of a city street → soft watercolor with luminous skies, pastel buildings, and that signature Ghibli warmth. Characters gain expressive eyes and painterly textures.',
    accent: '#e879f9',
    tags: ['Image Generation', 'Style Transfer', 'Art'],
  },
  {
    id: 'action-figure',
    category: 'Creative & Image',
    categoryIcon: '',
    title: 'Action Figure Box Creator',
    description: 'Turn yourself (or anyone) into a collectible action figure sealed in a blister pack. The most viral AI image trend of 2026 — wildly fun for gifts, LinkedIn, and social.',
    prompt: `Create a realistic product photo of a collectible action figure based on the person in this photo.

FIGURE: A ~6-inch plastic figure with visible articulation joints. Match the person's hair color, skin tone, and facial features as closely as possible.

OUTFIT: [describe their outfit or a fun version — e.g. "business casual with a tiny laptop", "superhero suit", "their everyday look"]

ACCESSORIES: 3–4 tiny items in a molded plastic tray that represent their personality or job — e.g. a miniature laptop, coffee cup, phone, book, or tool of their trade.

PACKAGING:
- Sealed blister pack with a printed cardboard backing
- Bold color scheme: [choose a color — e.g. electric blue and yellow, hot pink and white]
- Person's name in large font at the top
- A fun series name or tagline below — e.g. "AI Builder Series" or "The Hustler Collection"
- Small age rating badge in the corner (e.g. "Ages 25+")

STYLE: Toy-aisle retail photography, shot straight-on at eye level, bright even lighting, plain white or gradient background. Photorealistic packaging with slight plastic sheen on the blister.`,
    tools: ['ChatGPT (GPT-4o)', 'DALL-E 3', 'Midjourney'],
    exampleOutput: 'Photo of a woman in a blazer → sealed blister pack with a tiny figure holding a laptop and coffee cup. Packaging reads "SHYNA — The AI Builder Series · Ages 25+" in bold purple and gold.',
    accent: '#f43f5e',
    tags: ['Viral Trend', 'Fun', 'Portrait'],
  },
  {
    id: 'pixar-me',
    category: 'Creative & Image',
    categoryIcon: '',
    title: 'Pixar Character Creator',
    description: 'Transform any photo into a 3D Pixar-style animated character. Hugely popular on TikTok and Instagram — works great for profile photos and kids.',
    prompt: `Convert this photo into a 3D Pixar-style animated character.

STYLE RULES:
- Smooth, polished 3D render matching Pixar's signature look (think Incredibles, Up, Soul)
- Slightly larger, more expressive eyes while keeping the person's core facial features identical
- Soft, warm animation-style lighting with subtle rim light
- Clean, simple background — either a soft gradient or a simple environment that fits the character
- Maintain the person's hair color, skin tone, and defining features

IMPORTANT: Keep facial features recognizable. Do not fully cartoon-ify — this should look like THIS person rendered as a Pixar character, not a generic character.

Output at 1:1 ratio. If the likeness drifts, add: "preserve exact facial structure and features from the reference photo."`,
    tools: ['ChatGPT (GPT-4o)', 'DALL-E 3', 'Midjourney'],
    exampleOutput: 'Professional headshot → warm 3D Pixar render with the same hair, eyes, and smile — looks like a character from a new Pixar film about a tech entrepreneur.',
    accent: '#e879f9',
    tags: ['Viral Trend', 'Fun', 'Portrait', 'Pixar'],
  },
  {
    id: 'barbie-box',
    category: 'Creative & Image',
    categoryIcon: '',
    title: 'Barbie Doll Box',
    description: 'Package yourself as a Barbie (or Ken) in a branded display box complete with your name, edition title, and accessories. One of the most-shared AI image trends of 2025–2026.',
    prompt: `Create a product photo of a Barbie-style doll packaged in a display box, based on the person in this photo.

DOLL: A polished plastic doll with the person's hair color, skin tone, and facial features. Stylized but clearly recognizable.

PACKAGING:
- Clear-front pink (or [YOUR COLOR]) display box with a white or pastel backing card
- Person's name in the iconic Barbie font style at the top
- A fun edition title below — e.g. "AI Director Barbie", "Tech Founder Barbie", "World Traveler Barbie"
- 2–3 tiny accessories at the bottom of the box representing their personality (e.g. laptop, passport, coffee, camera)
- Small logo or tagline in the corner

STYLE: Clean retail product photography on a white background. Box should look like an actual Barbie package from the toy aisle — glossy, colorful, and collectible.`,
    tools: ['ChatGPT (GPT-4o)', 'DALL-E 3', 'Midjourney'],
    exampleOutput: 'Photo of a woman → pink display box with "SHYNA — AI Director Barbie" in hot pink font. Doll wears a blazer, box includes tiny laptop, coffee cup, and presentation clicker.',
    accent: '#f472b6',
    tags: ['Viral Trend', 'Fun', 'Portrait'],
  },
  {
    id: 'old-money',
    category: 'Creative & Image',
    categoryIcon: '',
    title: 'Old Money Portrait',
    description: 'Reimagine yourself as a 19th-century aristocrat in a museum-quality oil portrait. Massive trend on Pinterest and Instagram — stunning results.',
    prompt: `Transform this photo into a formal 19th-century aristocratic oil painting portrait.

STYLE:
- Rich, museum-quality oil painting technique — visible brushstrokes, deep shadows, warm candlelit glow
- Dark, dramatic background (deep burgundy, forest green, or navy) with subtle fabric or wood texture
- Clothing: transform into period-appropriate aristocratic attire — fine coat or gown, jewels, lace details
- Lighting: Rembrandt-style — single warm light source from the side, dramatic shadows
- Expression: poised, dignified, slightly serious — the bearing of old money

IMPORTANT: Keep the person's facial features, hair color, and likeness intact. Only transform the style, clothing, and setting.

Output at 2:3 portrait ratio. Style reference: John Singer Sargent or Thomas Gainsborough portraits.`,
    tools: ['ChatGPT (GPT-4o)', 'Midjourney', 'DALL-E 3'],
    exampleOutput: 'Modern photo → dramatic oil portrait in a burgundy velvet coat, candlelit library background. Looks like a painting hanging in a European estate. Jaw-dropping for framing or gifting.',
    accent: '#d97706',
    tags: ['Viral Trend', 'Art', 'Portrait'],
  },
  {
    id: 'polaroid-retro',
    category: 'Creative & Image',
    categoryIcon: '',
    title: '90s Polaroid Vintage Effect',
    description: 'Age any photo into a worn, nostalgic 1990s Polaroid with film grain, light leaks, and that faded analog warmth. Hugely popular with younger creators on TikTok.',
    prompt: `Take this photo and make it look like a genuine 1990s Polaroid print.

EFFECTS TO APPLY:
- Heavy film grain throughout the image
- Slight soft focus / blur at the edges, sharp in the center
- A warm yellow-orange light leak from one corner (like old film exposure)
- Slightly faded, desaturated colors — not black and white, just worn
- Subtle color shift: lift the shadows toward greenish-teal, push highlights to warm yellow
- Optional: add a slight white Polaroid border with a small handwritten caption below in a casual font

MOOD: Nostalgic, imperfect, authentic — the opposite of a crisp modern photo. Should look like it was found in a shoebox.

Do NOT make it look digitally filtered. The goal is organic, analog imperfection.`,
    tools: ['ChatGPT (GPT-4o)', 'DALL-E 3', 'Stable Diffusion'],
    exampleOutput: 'Crisp modern selfie → grainy, warm, slightly blurry Polaroid with a teal shadow shift and orange light leak. Caption reads "summer \'97" in handwriting. Instantly nostalgic.',
    accent: '#84cc16',
    tags: ['Vintage', 'Aesthetic', 'Photo Editing'],
  },
  {
    id: 'earnings-brief',
    category: 'Finance & Stocks',
    categoryIcon: '',
    title: 'Pre-Earnings Brief',
    description: 'Get a JPMorgan-style earnings brief before any company reports. Beat/miss history, consensus estimates, options-implied move, and a clear recommendation.',
    prompt: `Act as a JPMorgan equity analyst. For [TICKER], give me a pre-earnings brief covering:

1. Beat/miss history for the last 4 quarters (EPS and revenue vs. consensus)
2. Current consensus EPS and revenue estimate for this quarter
3. Options-implied move (±%) heading into earnings
4. The single biggest risk that could cause a miss
5. Buy, sell, or wait recommendation with one-sentence rationale

Be specific. Use actual numbers. Cite your sources.`,
    tools: ['Claude', 'ChatGPT', 'Perplexity'],
    exampleOutput: 'NVDA pre-earnings: beat 4/4 last quarters, consensus $0.89 EPS, implied ±8% move, risk = data center digestion cycle. Recommendation: Hold into print, add on any dip >5%.',
    accent: '#22c55e',
    tags: ['Earnings', 'Stock Analysis', 'Investing'],
  },
  {
    id: 'dcf-valuation',
    category: 'Finance & Stocks',
    categoryIcon: '',
    title: 'DCF Valuation + Sensitivity Table',
    description: 'Build a Goldman Sachs-style discounted cash flow model for any stock with a 3×3 sensitivity matrix across WACC and growth assumptions.',
    prompt: `Act as a Goldman Sachs equity analyst. For [TICKER], build a DCF valuation:

ASSUMPTIONS:
- 5-year free cash flow projection based on analyst consensus
- WACC = 10-year Treasury yield + 5.5% equity risk premium, adjusted for beta
- Terminal growth rate = 2.5%
- Tax rate = 24%

DELIVERABLES:
1. Year-by-year FCF projections (Years 1–5)
2. Terminal value calculation
3. Per-share intrinsic value
4. 3×3 sensitivity table: WACC (9%, 10%, 11%) × Terminal Growth (2%, 2.5%, 3%)
5. Verdict: overvalued / fairly valued / undervalued vs. current price

Replace [TICKER] with your stock. Verify the top 3 numbers against the most recent 10-K.`,
    tools: ['Claude', 'ChatGPT'],
    exampleOutput: 'AAPL DCF → intrinsic value $198/share. At 10% WACC / 2.5% growth. Sensitivity range: $171–$228. Current price $213 = 7.5% overvalued at base case.',
    accent: '#22c55e',
    tags: ['Valuation', 'DCF', 'Stock Analysis'],
  },
  {
    id: 'portfolio-risk',
    category: 'Finance & Stocks',
    categoryIcon: '',
    title: 'Portfolio Risk Audit',
    description: 'Bridgewater-inspired audit of your portfolio for hidden concentration risks, correlation traps, and recession vulnerabilities — with specific hedging options.',
    prompt: `Act as a Bridgewater portfolio analyst. Audit the following portfolio for risk:

[PASTE YOUR HOLDINGS: ticker, % allocation]

Analyze:
1. Correlation risk — which positions move together and amplify losses
2. Sector concentration — any sector over 25% of portfolio
3. Geographic concentration — US vs. international exposure
4. Recession drawdown estimate — based on 2008 and 2020 analogues
5. Three specific hedging options ranked by cost and effectiveness

Output a risk score (1–10) for each category and an overall portfolio risk rating.`,
    tools: ['Claude', 'ChatGPT', 'Perplexity'],
    exampleOutput: 'Portfolio of 60% tech, 20% bonds, 20% real estate → Correlation risk: 8/10 (tech + bonds both rate-sensitive). Recession drawdown estimate: -38%. Hedge: add 5% gold ETF + 2% VIX calls.',
    accent: '#22c55e',
    tags: ['Portfolio', 'Risk Management', 'Investing'],
  },
  {
    id: 'competitive-moat',
    category: 'Finance & Stocks',
    categoryIcon: '',
    title: 'Competitive Moat Comparison',
    description: 'Bain-style analysis comparing the top competitors in any sector on margins, moats, and market share — ending with one clear stock pick.',
    prompt: `Act as a Bain & Company analyst. Compare the top 3 competitors in [SECTOR/INDUSTRY]:

For each company, analyze:
1. Gross and operating margins (last 12 months)
2. Economic moat — switching costs, network effects, cost advantages, intangibles
3. Market share trajectory — gaining or losing ground?
4. Management quality — capital allocation track record
5. Biggest threat to their position in the next 2 years

Final output: rank all three and recommend ONE to own with a 12-month price target rationale.`,
    tools: ['Claude', 'ChatGPT', 'Perplexity'],
    exampleOutput: 'Cloud infrastructure (AWS vs. Azure vs. GCP) → Azure wins on enterprise moat (Office 365 lock-in), AWS on margins, GCP on AI differentiation. Pick: MSFT for risk-adjusted return.',
    accent: '#22c55e',
    tags: ['Competitive Analysis', 'Sector Research', 'Stocks'],
  },
  {
    id: 'swot-sharp',
    category: 'Strategy & Business',
    categoryIcon: '',
    title: 'Sharp SWOT Analysis',
    description: 'A no-fluff SWOT that forces specificity — every point must be a verifiable fact or signal, no generic statements allowed.',
    prompt: `Do a SWOT analysis for [COMPANY OR IDEA].

RULES:
- No generic statements. Every point must be a specific, verifiable fact or observable signal.
- Strengths and weaknesses = internal. Opportunities and threats = external.
- Maximum 3 points per quadrant — force prioritization.
- End with: the single biggest strategic priority based on this SWOT.

Format as a clean 2×2 table followed by a one-paragraph strategic recommendation.`,
    tools: ['Claude', 'ChatGPT'],
    exampleOutput: 'Starbucks SWOT → Strength: 37,000 locations as distribution moat. Weakness: $14B debt from buybacks. Opportunity: China recovery. Threat: at-home espresso machines. Priority: fix unit economics in China before expanding further.',
    accent: '#f59e0b',
    tags: ['Strategy', 'Business Analysis', 'Frameworks'],
  },
  {
    id: 'competitor-wedge',
    category: 'Strategy & Business',
    categoryIcon: '',
    title: 'Competitive Positioning Wedge',
    description: 'Find the exact gap in your competitor\'s armor — their pricing weaknesses, top customer complaints, and the one positioning wedge you should build around.',
    prompt: `I'm building a product/business that competes with [COMPETITOR NAME].

Research and give me:
1. Their pricing model and where customers feel it's unfair or confusing
2. Top 3 customer complaints (pull from G2, Trustpilot, Reddit, app store reviews)
3. The segment they underserve or ignore
4. Their messaging blind spots — what they never say but customers want to hear
5. The ONE positioning wedge I should build my entire go-to-market around

Be brutally specific. I need to win, not just differentiate.`,
    tools: ['Claude', 'ChatGPT', 'Perplexity'],
    exampleOutput: 'Competing with Notion → Complaints: too complex for non-technical users, poor mobile UX, slow customer support. Underserved: solopreneurs who want done-for-you templates. Wedge: "The simple workspace that works out of the box."',
    accent: '#f59e0b',
    tags: ['Competitive Intelligence', 'GTM', 'Positioning'],
  },
  {
    id: 'sop-generator',
    category: 'Productivity',
    categoryIcon: '',
    title: 'SOP Generator',
    description: 'Turn any repeatable task into a bulletproof Standard Operating Procedure — with steps, rationale, and a done checklist. Reddit\'s r/smallbusiness reports 30–50% faster onboarding.',
    prompt: `Create a Standard Operating Procedure (SOP) for: [TASK NAME]

Format:
- Title and purpose (1 sentence)
- Who this is for
- Numbered steps — each with: (a) what to do, (b) why it matters, (c) what "done" looks like
- Common mistakes to avoid
- End-of-task checklist

Tone: clear, direct, no jargon. Written for someone doing this for the first time.`,
    tools: ['Claude', 'ChatGPT'],
    exampleOutput: 'SOP for "Onboarding a new client" → 8 steps from contract signing to first deliverable. Includes: why each handoff matters, common drop-off points, and a 12-item checklist the client signs off on.',
    accent: '#818cf8',
    tags: ['Operations', 'Business', 'Process'],
  },
  {
    id: 'prompt-improver',
    category: 'Productivity',
    categoryIcon: '',
    title: 'The Prompt Improver',
    description: 'The most-shared meta-prompt on Reddit. Paste any prompt and get back a version that\'s 40–50% more effective with a clear role, format, constraints, and examples.',
    prompt: `Here is a prompt I'm currently using:

[PASTE YOUR PROMPT HERE]

Improve it by adding:
1. A clear expert role/persona for the AI
2. Specific output format (length, structure, tone)
3. Constraints — what to avoid or never do
4. 2 example outputs showing exactly what good looks like
5. A "success criteria" sentence — how to know if the output is right

Return the improved prompt only. No explanation.`,
    tools: ['Claude', 'ChatGPT', 'Gemini'],
    exampleOutput: 'Input: "Write me a cold email." Output: A 400-word prompt with a B2B sales expert persona, 150-word length constraint, forbidden phrases list, 2 example emails, and success criteria: "prospect replies within 48 hours."',
    accent: '#818cf8',
    tags: ['Prompt Engineering', 'Meta-Prompt', 'Productivity'],
  },
];

const PROMPT_CATEGORIES = ['All', 'Creative & Image', 'Finance & Stocks', 'Strategy & Business', 'Productivity'];

// ─── AI Readiness Quiz Data ────────────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    id: 1,
    question: 'How would you describe your organization\'s current AI usage?',
    options: [
      { text: 'We don\'t use AI yet', score: 0 },
      { text: 'Experimenting with tools like ChatGPT informally', score: 1 },
      { text: 'A few teams using AI tools with some structure', score: 2 },
      { text: 'Multiple departments with AI initiatives underway', score: 3 },
      { text: 'AI is integrated into core products and operations', score: 4 },
    ],
  },
  {
    id: 2,
    question: 'Do you have a formal AI governance policy or framework?',
    options: [
      { text: 'No policy at all', score: 0 },
      { text: 'Informal guidelines but nothing documented', score: 1 },
      { text: 'Policy in draft / under review', score: 2 },
      { text: 'Published policy, limited enforcement', score: 3 },
      { text: 'Comprehensive governance with active review cycles', score: 4 },
    ],
  },
  {
    id: 3,
    question: 'How would you rate your organization\'s data infrastructure?',
    options: [
      { text: 'Data is siloed and hard to access', score: 0 },
      { text: 'Some centralization but lots of manual work', score: 1 },
      { text: 'Data warehouse exists, quality is inconsistent', score: 2 },
      { text: 'Clean data pipelines, most teams can access', score: 3 },
      { text: 'Modern data platform, real-time, high quality', score: 4 },
    ],
  },
  {
    id: 4,
    question: 'What is your executive team\'s stance on AI investment?',
    options: [
      { text: 'Skeptical — waiting to see if it\'s a fad', score: 0 },
      { text: 'Aware but no budget allocated', score: 1 },
      { text: 'Interested, some budget discussions happening', score: 2 },
      { text: 'Committed — AI is in the strategic plan', score: 3 },
      { text: 'Fully committed — AI is core to company strategy', score: 4 },
    ],
  },
  {
    id: 5,
    question: 'How would you describe AI literacy across your workforce?',
    options: [
      { text: 'Most employees have never used AI tools', score: 0 },
      { text: 'A few early adopters using AI personally', score: 1 },
      { text: 'Growing awareness, some informal training', score: 2 },
      { text: 'Structured AI literacy programs in place', score: 3 },
      { text: 'AI fluency is a hiring and promotion criterion', score: 4 },
    ],
  },
  {
    id: 6,
    question: 'How do you currently measure ROI on AI initiatives?',
    options: [
      { text: 'We don\'t measure — it\'s all anecdotal', score: 0 },
      { text: 'Tracking some usage metrics', score: 1 },
      { text: 'Measuring cost savings in some areas', score: 2 },
      { text: 'Clear KPIs tied to business outcomes', score: 3 },
      { text: 'Full business case model with continuous optimization', score: 4 },
    ],
  },
];

const MATURITY_LEVELS = [
  { min: 0, max: 6, level: 'Foundational', emoji: '○', color: '#64748b', description: 'You\'re at the starting line — and that\'s fine. Most organizations are here. The opportunity is enormous, and the first movers in your industry are just 12-18 months ahead.', recommendations: ['Start with an AI policy (1-pager is enough)', 'Pick 2-3 high-ROI pilot use cases', 'Identify your AI champion at the VP level', 'Audit your data quality in one business unit'] },
  { min: 7, max: 12, level: 'Developing', emoji: '◑', color: '#fbbf24', description: 'You have real momentum. The risk now is fragmented experimentation without a unifying strategy. Your competitors at this stage who accelerate win; those who stall fall behind permanently.', recommendations: ['Formalize governance before scaling', 'Launch a Center of Excellence (even small)', 'Build a use case prioritization framework', 'Invest in AI literacy for mid-management'] },
  { min: 13, max: 18, level: 'Scaling', emoji: '◕', color: '#818cf8', description: 'Strong foundation. You\'re ahead of most organizations. The next phase is moving from "AI experiments" to "AI-native operations" — embedding AI into how work gets done, not just as a tool on top.', recommendations: ['Build FinOps for AI — track spend and ROI rigorously', 'Evaluate agentic AI for high-volume workflows', 'Deepen regulatory compliance posture', 'Create AI talent pathways and retention programs'] },
  { min: 19, max: 24, level: 'Leading', emoji: '●', color: '#34d399', description: 'You\'re in the top tier of AI-mature enterprises. The focus now is on defensible AI advantage — proprietary models, data moats, and governance that enables speed rather than blocking it.', recommendations: ['Explore fine-tuned or proprietary models', 'Build AI into product differentiators', 'Share your governance playbook (thought leadership)', 'Develop AI vendor evaluation rigor at board level'] },
];

// ─── Resource Hub Data ─────────────────────────────────────────────────────────
const RESOURCES = [
  {
    category: 'AI Governance',
    icon: 'Scale',
    accent: '#818cf8',
    items: [
      { title: 'NIST AI Risk Management Framework', url: 'https://www.nist.gov/artificial-intelligence/ai-risk-management-framework', desc: 'The gold standard for enterprise AI governance. Start here.' },
      { title: 'EU AI Act Full Text', url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32024R1689', desc: 'If you touch EU markets, this is now law. Know your obligations.' },
      { title: 'ISO/IEC 42001 — AI Management System', url: 'https://www.iso.org/standard/81230.html', desc: 'The ISO standard for AI management. Pairs with ISO 27001.' },
      { title: 'FDA AI/ML-Based SaMD Action Plan', url: 'https://www.fda.gov/medical-devices/software-medical-device-samd/artificial-intelligence-and-machine-learning-software-medical-device', desc: 'For healthcare AI: FDA\'s evolving regulatory approach to AI in medical devices.' },
    ],
  },
  {
    category: 'FinOps for AI',
    icon: 'Coins',
    accent: '#fbbf24',
    items: [
      { title: 'FinOps Foundation — AI Cost Optimization', url: 'https://www.finops.org/wg/ai/', desc: 'Emerging best practices for managing AI/ML spend in the enterprise.' },
      { title: 'AWS Cost Optimization for AI/ML', url: 'https://aws.amazon.com/blogs/aws-cost-management/category/artificial-intelligence/', desc: 'Practical guides for reducing AI inference and training costs on AWS.' },
      { title: 'Model Token Cost Calculator', url: 'https://openai.com/api/pricing/', desc: 'OpenAI pricing — use this to build your AI unit economics model.' },
      { title: 'Anthropic Claude Pricing', url: 'https://www.anthropic.com/pricing', desc: 'Claude API pricing tiers — strong for long-context enterprise use cases.' },
    ],
  },
  {
    category: 'Regulated Industries',
    icon: 'Cross',
    accent: '#34d399',
    items: [
      { title: 'Coalition for Health AI (CHAI)', url: 'https://chai.org/', desc: 'Best practices for responsible AI in healthcare. CHAI blueprint is excellent.' },
      { title: 'ONC Artificial Intelligence Strategy', url: 'https://www.healthit.gov/topic/scientific-initiatives/artificial-intelligence', desc: 'US federal health IT AI strategy — key for hospital systems.' },
      { title: 'FINRA AI in Financial Services', url: 'https://www.finra.org/rules-guidance/key-topics/artificial-intelligence', desc: 'Regulatory guidance for AI use in brokerage and financial advisory.' },
      { title: 'CFPB AI and Algorithmic Bias', url: 'https://www.consumerfinance.gov/about-us/blog/cfpb-issues-guidance-on-credit-denials-by-lenders-using-artificial-intelligence/', desc: 'Fair lending + AI — essential for any financial institution using AI in credit.' },
    ],
  },
  {
    category: 'Agentic AI',
    icon: 'Bot',
    accent: '#38bdf8',
    items: [
      { title: 'Anthropic Model Specification', url: 'https://www.anthropic.com/research/model-specification', desc: 'How Claude is designed to behave. Essential reading for enterprise AI buyers.' },
      { title: 'OpenAI Agents Overview', url: 'https://platform.openai.com/docs/guides/agents-overview', desc: 'Official guide to building and managing AI agents with the OpenAI platform.' },
      { title: 'LangChain Documentation', url: 'https://docs.langchain.com/', desc: 'The most-used framework for building agentic AI applications.' },
      { title: 'Google Agent Development Kit', url: 'https://google.github.io/adk-docs/', desc: 'Google\'s ADK for building production-grade AI agents on Gemini.' },
    ],
  },
  {
    category: 'Enterprise AI Tools',
    icon: 'Wrench',
    accent: '#fb923c',
    items: [
      { title: 'Microsoft Copilot for M365', url: 'https://copilot.microsoft.com/', desc: 'If your org uses Office 365, Copilot is the fastest AI ROI play available.' },
      { title: 'Salesforce Einstein AI', url: 'https://www.salesforce.com/products/einstein/overview/', desc: 'AI embedded in CRM — strong for sales orgs already in the Salesforce ecosystem.' },
      { title: 'AWS Bedrock', url: 'https://aws.amazon.com/bedrock/', desc: 'Enterprise-grade access to Claude, Llama, Titan, and others via one API.' },
      { title: 'Google Vertex AI', url: 'https://cloud.google.com/vertex-ai', desc: 'Full ML platform — strong for healthcare and regulated industries on GCP.' },
    ],
  },
];


// ─── Feedback widget ──────────────────────────────────────────────────────────
function FeedbackWidget({ projectTitle, accent }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);

  const submit = () => {
    if (!rating) return;
    const feedbacks = JSON.parse(localStorage.getItem('builditai_feedback') || '[]');
    feedbacks.push({ project: projectTitle, rating, text, date: new Date().toISOString() });
    localStorage.setItem('builditai_feedback', JSON.stringify(feedbacks));
    setSent(true);
    setTimeout(() => { setSent(false); setOpen(false); setRating(0); setText(''); }, 2000);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 99, padding: '5px 12px', color: '#64748b', fontFamily: 'Space Mono', fontSize: 11, cursor: 'pointer', marginTop: 16, transition: 'all 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accent + '60'; e.currentTarget.style.color = accent; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#64748b'; }}
      >
        Leave feedback
      </button>

      {open && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: '110%', left: 0, zIndex: 200, background: '#0d0d1a', border: `1px solid ${accent}40`, borderRadius: 16, padding: 20, width: 280, boxShadow: `0 20px 60px rgba(0,0,0,0.6)` }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
              <div style={{ color: '#f1f5f9', fontFamily: 'Space Grotesk', fontWeight: 600 }}>Thanks for your feedback!</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#f1f5f9', fontFamily: 'Space Grotesk', fontWeight: 600, marginBottom: 12 }}>Rate {projectTitle}</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setRating(s)} onMouseEnter={() => setHovered(s)} onMouseLeave={() => setHovered(0)}
                    style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', transition: 'transform 0.1s', transform: hovered >= s || rating >= s ? 'scale(1.2)' : 'scale(1)', filter: hovered >= s || rating >= s ? 'none' : 'grayscale(1) opacity(0.3)' }}>
                    ★
                  </button>
                ))}
              </div>
              <textarea value={text} onChange={e => setText(e.target.value)} placeholder="What do you think? (optional)"
                rows={3} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px', color: '#f1f5f9', fontFamily: 'Space Mono', fontSize: 12, resize: 'none', outline: 'none', marginBottom: 12, boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setOpen(false)} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 0', color: '#64748b', fontFamily: 'Space Mono', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                <button onClick={submit} disabled={!rating} style={{ flex: 2, background: rating ? `linear-gradient(135deg,${accent},${accent}bb)` : 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 8, padding: '8px 0', color: rating ? '#07070f' : '#475569', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 12, cursor: rating ? 'pointer' : 'default' }}>Submit →</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Project card ─────────────────────────────────────────────────────────────
function ProjectCard({ project, onClick }) {
  const isModal = project.id === 'sec' || project.id === 'tipcalc' || project.id === 'valuestream';
  return (
    <div
      onClick={() => isModal ? onClick(project) : project.url !== '#' && window.open(project.url, '_blank', 'noopener,noreferrer')}
      style={{ cursor: isModal || project.url !== '#' ? 'pointer' : 'default', background: '#0A0A0A', padding: 32, transition: 'background 0.2s', position: 'relative' }}
      onMouseEnter={e => { e.currentTarget.style.background = '#111'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#0A0A0A'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <span style={{ display: 'flex', alignItems: 'center', color: project.accent }}><Icon name={project.icon} size={28} color={project.accent} strokeWidth={1.5} /></span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 10, fontFamily: 'Space Mono', fontWeight: 700, letterSpacing: '0.06em', backgroundColor: project.status === 'Live' ? 'rgba(52,211,153,0.12)' : project.status === 'Beta' ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.06)', color: project.status === 'Live' ? '#34d399' : project.status === 'Beta' ? '#fbbf24' : '#555' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', display: 'inline-block', backgroundColor: project.status === 'Live' ? '#34d399' : project.status === 'Beta' ? '#fbbf24' : '#555' }} />
          {project.status}
        </span>
      </div>
      <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: project.accent, fontFamily: 'Space Mono', marginBottom: 10, opacity: 0.85 }}>{project.tag}</div>
      <h3 style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF', marginBottom: 10, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em', lineHeight: 1.2 }}>{project.title}</h3>
      <p style={{ fontSize: 13, color: '#666', lineHeight: 1.7, marginBottom: 20 }}>{project.description}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {project.tech.map(t => (
          <span key={t} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 99, background: 'rgba(255,255,255,0.05)', color: '#555', fontFamily: 'Space Mono', border: '1px solid rgba(255,255,255,0.07)' }}>{t}</span>
        ))}
      </div>
      <FeedbackWidget projectTitle={project.title} accent={project.accent} />
    </div>
  );
}

// ─── Prompt Card ───────────────────────────────────────────────────────────────
function PromptCard({ prompt }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyPrompt = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(prompt.prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ background: '#FDFCFA', border: '1px solid #E8E6E1', borderRadius: 16, padding: 28, transition: 'all 0.2s', position: 'relative' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#D0CEC9'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E6E1'; e.currentTarget.style.boxShadow = ''; }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: prompt.accent, fontFamily: 'Space Mono', marginBottom: 6 }}>
            {prompt.category}
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1A1916', fontFamily: 'Space Grotesk', lineHeight: 1.3, letterSpacing: '-0.02em' }}>{prompt.title}</h3>
        </div>
        <button onClick={copyPrompt} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, background: copied ? 'rgba(52,211,153,0.1)' : '#F0EEE9', border: `1px solid ${copied ? '#34d39950' : '#E0DDD7'}`, borderRadius: 8, padding: '7px 14px', color: copied ? '#1a7a5e' : '#6B6B6B', fontFamily: 'Space Mono', fontSize: 11, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
          {copied ? '✓ Copied' : '⎘ Copy'}
        </button>
      </div>

      <p style={{ fontSize: 13, color: '#6B6B6B', lineHeight: 1.7, marginBottom: 16 }}>{prompt.description}</p>

      {/* Tools */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {prompt.tools.map(t => (
          <span key={t} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: `${prompt.accent}12`, color: prompt.accent, fontFamily: 'Space Mono', border: `1px solid ${prompt.accent}28` }}>{t}</span>
        ))}
      </div>

      {/* Expand toggle */}
      <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: '#9B9790', fontFamily: 'Space Mono', fontSize: 11, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6, marginBottom: expanded ? 16 : 0 }}>
        <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        {expanded ? 'Hide prompt' : 'See full prompt + example output'}
      </button>

      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Full prompt */}
          <div style={{ background: '#F5F3EF', border: '1px solid #E8E6E1', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B9790', fontFamily: 'Space Mono', marginBottom: 10 }}>Prompt</div>
            <pre style={{ fontFamily: 'Space Mono', fontSize: 12, color: '#4A4845', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{prompt.prompt}</pre>
          </div>
          {/* Example output */}
          <div style={{ background: `${prompt.accent}08`, border: `1px solid ${prompt.accent}22`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: prompt.accent, fontFamily: 'Space Mono', marginBottom: 8 }}>Example Output</div>
            <p style={{ fontSize: 13, color: '#6B6B6B', lineHeight: 1.7, margin: 0 }}>{prompt.exampleOutput}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Prompt Library Section ────────────────────────────────────────────────────
function PromptLibrary() {
  const [activeCategory, setActiveCategory] = useState('All');
  const ink = '#1A1916';
  const muted = '#6B6B6B';
  const accent = '#6D28D9';

  const filtered = activeCategory === 'All' ? PROMPTS : PROMPTS.filter(p => p.category === activeCategory);

  return (
    <section id="prompts" style={{ background: '#FDFCFA', padding: '80px 32px', borderTop: '1px solid #E8E6E1', borderBottom: '1px solid #E8E6E1' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 56, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: accent, fontFamily: 'Space Mono', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>Prompt Library</div>
            <h2 style={{ fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, letterSpacing: '-0.03em', color: ink, lineHeight: 1.05 }}>Prompts That Work</h2>
          </div>
          <p style={{ fontSize: 14, color: muted, maxWidth: 360, lineHeight: 1.7, letterSpacing: '-0.01em' }}>
            For image generation, video creation, and creative AI. Copy with one click.
          </p>
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 40, flexWrap: 'wrap' }}>
          {PROMPT_CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              style={{ background: activeCategory === cat ? ink : 'transparent', border: `1px solid ${activeCategory === cat ? ink : '#D0CEC9'}`, borderRadius: 99, padding: '8px 20px', color: activeCategory === cat ? '#FDFCFA' : muted, fontFamily: 'Space Mono', fontSize: 12, cursor: 'pointer', transition: 'all 0.2s' }}>
              {cat}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(380px,1fr))', gap: 16 }}>
          {filtered.map(p => <PromptCard key={p.id} prompt={p} />)}
        </div>
      </div>
    </section>
  );
}

// ─── AI Readiness Calculator ───────────────────────────────────────────────────
function AIReadinessCalculator() {
  const [step, setStep] = useState(0); // 0 = intro, 1-6 = questions, 7 = results
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);

  const totalQuestions = QUIZ_QUESTIONS.length;
  const isIntro = step === 0;
  const isResults = step === totalQuestions + 1;
  const currentQ = !isIntro && !isResults ? QUIZ_QUESTIONS[step - 1] : null;

  const totalScore = Object.values(answers).reduce((sum, s) => sum + s, 0);
  const maturity = MATURITY_LEVELS.find(l => totalScore >= l.min && totalScore <= l.max) || MATURITY_LEVELS[0];
  const maxScore = totalQuestions * 4;

  const handleAnswer = (optionIndex, score) => {
    setSelected(optionIndex);
    setTimeout(() => {
      setAnswers(prev => ({ ...prev, [step]: score }));
      setStep(s => s + 1);
      setSelected(null);
    }, 400);
  };

  const restart = () => { setStep(0); setAnswers({}); setSelected(null); };

  return (
    <section id="calculator" style={{ background: '#000000', padding: '80px 32px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: '#7c3aed', fontFamily: 'Space Mono', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>Free Tool</div>
          <h2 style={{ fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, letterSpacing: '-0.03em', color: '#FFFFFF', lineHeight: 1.05, marginBottom: 16 }}>AI Readiness Assessment</h2>
          <p style={{ fontSize: 15, color: '#666', maxWidth: 480, lineHeight: 1.7 }}>
            6 questions. 3 minutes. Know exactly where your organization stands on the AI maturity curve.
          </p>
        </div>

        <div style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderRadius: 20, padding: '44px 48px' }}>

          {/* Intro */}
          {isIntro && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 24, color: '#7c3aed', fontFamily: 'Space Mono' }}>AI</div>
              <h3 style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Space Grotesk', marginBottom: 16 }}>Where does your org stand?</h3>
              <p style={{ color: '#64748b', lineHeight: 1.7, marginBottom: 12, fontSize: 15 }}>This assessment benchmarks your organization across 6 AI readiness dimensions — strategy, data, talent, tools, buy-in, and ROI measurement.</p>
              <p style={{ color: '#475569', lineHeight: 1.7, marginBottom: 32, fontSize: 13, fontFamily: 'Space Mono' }}>6 questions · 3 minutes · instant results.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 40, flexWrap: 'wrap' }}>
                {[['TrendingUp', '6 Questions'], ['Clock', '3 Minutes'], ['Target', 'Instant Results']].map(([iconName, label]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13, fontFamily: 'Space Mono' }}>
                    <Icon name={iconName} size={16} color="#7c3aed" strokeWidth={2} />{label}
                  </div>
                ))}
              </div>
              <button onClick={() => setStep(1)} style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', borderRadius: 14, padding: '16px 40px', color: '#fff', fontFamily: 'Space Grotesk', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
                Start Assessment →
              </button>
            </div>
          )}

          {/* Questions */}
          {currentQ && (
            <div>
              {/* Progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 36 }}>
                <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${((step - 1) / totalQuestions) * 100}%`, background: 'linear-gradient(90deg,#7c3aed,#818cf8)', borderRadius: 99, transition: 'width 0.4s' }} />
                </div>
                <span style={{ fontSize: 11, color: '#475569', fontFamily: 'Space Mono', whiteSpace: 'nowrap' }}>
                  {step - 1} / {totalQuestions}
                </span>
              </div>

              <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7c3aed', fontFamily: 'Space Mono', marginBottom: 12 }}>Question {step}</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Space Grotesk', marginBottom: 28, lineHeight: 1.4 }}>{currentQ.question}</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {currentQ.options.map((opt, i) => (
                  <button key={i} onClick={() => handleAnswer(i, opt.score)}
                    style={{ background: selected === i ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.03)', border: `1px solid ${selected === i ? 'rgba(124,58,237,0.5)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 14, padding: '16px 20px', color: selected === i ? '#a78bfa' : '#94a3b8', fontFamily: 'Space Grotesk', fontSize: 14, textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { if (selected === null) { e.currentTarget.style.borderColor = 'rgba(124,58,237,0.35)'; e.currentTarget.style.color = '#f1f5f9'; }}}
                    onMouseLeave={e => { if (selected === null) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#94a3b8'; }}}>
                    <span style={{ marginRight: 12, fontSize: 12, fontFamily: 'Space Mono', color: '#475569' }}>{String.fromCharCode(65 + i)}.</span>
                    {opt.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {isResults && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <div style={{ fontSize: 56, marginBottom: 12 }}>{maturity.emoji}</div>
                <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: maturity.color, fontFamily: 'Space Mono', marginBottom: 8 }}>Your AI Maturity Level</div>
                <h3 style={{ fontSize: 36, fontWeight: 700, fontFamily: 'Space Grotesk', color: maturity.color, marginBottom: 8 }}>{maturity.level}</h3>
                <div style={{ fontSize: 15, color: '#64748b', fontFamily: 'Space Mono', marginBottom: 20 }}>Score: {totalScore} / {maxScore}</div>

                {/* Score bar */}
                <div style={{ maxWidth: 400, margin: '0 auto 24px', height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(totalScore / maxScore) * 100}%`, background: `linear-gradient(90deg,#7c3aed,${maturity.color})`, borderRadius: 99, transition: 'width 1s ease' }} />
                </div>

                <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, maxWidth: 500, margin: '0 auto' }}>{maturity.description}</p>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 16 }}>Priority Recommendations</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {maturity.recommendations.map((rec, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: `${maturity.color}20`, border: `1px solid ${maturity.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: maturity.color, fontFamily: 'Space Mono', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                      <span style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dimension breakdown */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 16 }}>Dimension Breakdown</div>
                {[
                  ['AI Usage & Adoption', 0],
                  ['Governance & Policy', 1],
                  ['Data Infrastructure', 2],
                  ['Executive Buy-in', 3],
                  ['AI Literacy', 4],
                  ['ROI Measurement', 5],
                ].map(([dim, qIdx]) => {
                  const score = answers[qIdx + 1] ?? 0;
                  const dimColor = score >= 3 ? '#34d399' : score >= 2 ? '#fbbf24' : score >= 1 ? '#fb923c' : '#f87171';
                  const dimLabel = score >= 3 ? 'Strong' : score >= 2 ? 'Developing' : score >= 1 ? 'Early Stage' : 'Not Started';
                  return (
                    <div key={dim} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ width: 130, fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>{dim}</div>
                      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 99 }}>
                        <div style={{ height: '100%', width: `${(score / 4) * 100}%`, background: dimColor, borderRadius: 99, transition: 'width 0.8s ease' }} />
                      </div>
                      <div style={{ width: 70, fontSize: 11, color: dimColor, fontFamily: 'Space Mono', textAlign: 'right', flexShrink: 0 }}>{dimLabel}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={restart} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 24px', color: '#94a3b8', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>↩ Retake Assessment</button>
                <a href="#newsletter" style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', borderRadius: 12, padding: '12px 24px', color: '#fff', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>Get AI Strategies → Subscribe</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Resource Hub ──────────────────────────────────────────────────────────────
function ResourceHub() {
  const [activeCategory, setActiveCategory] = useState(RESOURCES[0].category);
  const active = RESOURCES.find(r => r.category === activeCategory);

  const ink = '#1A1916';
  const muted = '#6B6B6B';
  const accent = '#6D28D9';

  return (
    <section id="resources" style={{ background: '#FDFCFA', padding: '80px 32px', borderTop: '1px solid #E8E6E1' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 56, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: accent, fontFamily: 'Space Mono', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>Resource Hub</div>
            <h2 style={{ fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, letterSpacing: '-0.03em', color: ink, lineHeight: 1.05 }}>Worth Bookmarking</h2>
          </div>
          <p style={{ fontSize: 14, color: muted, maxWidth: 360, lineHeight: 1.7, letterSpacing: '-0.01em' }}>
            No link dumps — only the tools, frameworks, and reads that actually matter.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 32, alignItems: 'start' }}>
          {/* Category sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {RESOURCES.map(cat => (
              <button key={cat.category} onClick={() => setActiveCategory(cat.category)}
                style={{ background: 'transparent', border: 'none', borderRadius: 8, padding: '10px 14px', color: activeCategory === cat.category ? ink : muted, fontFamily: 'Space Grotesk', fontSize: 13, fontWeight: activeCategory === cat.category ? 600 : 500, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s', borderLeft: `2px solid ${activeCategory === cat.category ? ink : 'transparent'}` }}>
                <Icon name={cat.icon} size={15} color={activeCategory === cat.category ? ink : muted} strokeWidth={1.5} />
                {cat.category}
              </button>
            ))}
          </div>

          {/* Resource cards */}
          {active && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {active.items.map((item, i) => (
                <a key={i} href={item.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', background: '#FFFFFF', border: '1px solid #E8E6E1', borderRadius: 12, padding: '20px 24px', textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#D0CEC9'; e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E6E1'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: ink, fontFamily: 'Space Grotesk', marginBottom: 5, letterSpacing: '-0.01em' }}>{item.title}</div>
                      <div style={{ fontSize: 13, color: muted, lineHeight: 1.6 }}>{item.desc}</div>
                    </div>
                    <span style={{ color: active.accent, fontSize: 16, flexShrink: 0, marginTop: 2 }}>→</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── SEC Tool modal ────────────────────────────────────────────────────────────
// Fetches real SEC EDGAR data via our own serverless function (/api/sec), which
// calls the SEC server-side with a declared User-Agent (the SEC endpoints don't
// allow direct browser/CORS access).
// Helper: fetch with timeout
function fetchWithTimeout(url, ms = 15000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(id));
}

async function fetchSECData(ticker) {
  let res;
  try {
    res = await fetchWithTimeout(`/api/sec?ticker=${encodeURIComponent(ticker)}`, 30000);
  } catch (e) {
    if (e.name === 'AbortError') {
      throw new Error(`Timed out fetching SEC data for ${ticker}. SEC EDGAR may be slow — try again in a moment.`);
    }
    throw new Error("Couldn't reach the server, check your connection and try again.");
  }
  let body = null;
  try { body = await res.json(); } catch (_) {}
  if (!res.ok) {
    // Show the server's own message (e.g. unknown ticker) when it sent one
    throw new Error(body?.error || `The server returned an error (HTTP ${res.status}). Please try again in a moment.`);
  }
  if (!body) throw new Error('The server sent an unexpected response. Please try again in a moment.');
  // body: { cik, companyName, ticker, ocf, fcfInvest, netIncome, revenue, eps, debt, assets, equity, capex, prices }
  return body;
}

function computeAnalysis(data) {
  const { ocf, netIncome, revenue, eps, debt, assets, equity, capex, companyName, ticker } = data;

  const fmt = (v) => {
    if (v === null || v === undefined || isNaN(v)) return 'N/A';
    const abs = Math.abs(v);
    if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    return `$${v.toFixed(2)}`;
  };

  const getVal = (arr, idx = 0) => arr?.[idx]?.val ?? null;
  const getYear = (arr, idx = 0) => arr?.[idx]?.end?.slice(0, 4) ?? '';

  // Current year values
  const ocfNow = getVal(ocf, 0);
  const ocfPrev = getVal(ocf, 1);
  // getVal(ocf, 2) used for 3yr trend if needed
  const capexNow = getVal(capex, 0);
  const fcf = ocfNow != null && capexNow != null ? ocfNow - capexNow : null;
  const niNow = getVal(netIncome, 0);
  const niPrev = getVal(netIncome, 1);
  const revNow = getVal(revenue, 0);
  const revPrev = getVal(revenue, 1);
  const debtNow = getVal(debt, 0);
  const assetsNow = getVal(assets, 0);
  const equityNow = getVal(equity, 0);
  const epsNow = getVal(eps, 0);
  const epsPrev = getVal(eps, 1);

  // Derived ratios
  const ocfMargin = (ocfNow != null && revNow) ? (ocfNow / revNow) : null;
  const fcfMargin = (fcf != null && revNow) ? (fcf / revNow) : null;
  const debtToEquity = (debtNow != null && equityNow) ? (debtNow / equityNow) : null;
  const roe = (niNow != null && equityNow) ? (niNow / equityNow) : null;
  const ocfGrowth = (ocfNow != null && ocfPrev) ? ((ocfNow - ocfPrev) / Math.abs(ocfPrev)) : null;
  const revGrowth = (revNow != null && revPrev) ? ((revNow - revPrev) / Math.abs(revPrev)) : null;
  const niGrowth = (niNow != null && niPrev) ? ((niNow - niPrev) / Math.abs(niPrev)) : null;
  const epsGrowth = (epsNow != null && epsPrev) ? ((epsNow - epsPrev) / Math.abs(epsPrev)) : null;
  // Accruals quality: net income vs OCF (lower = better quality earnings)
  const accrualsRatio = (niNow != null && ocfNow && assetsNow) ? ((niNow - ocfNow) / assetsNow) : null;

  const pct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : 'N/A';
  const yearNow = getYear(ocf, 0) || getYear(revenue, 0);

  // Score signals (0–10 each, higher = better)
  const signals = [];
  if (ocfMargin != null) signals.push({ label: 'OCF Margin', score: Math.min(10, ocfMargin * 50), val: pct(ocfMargin) });
  if (fcfMargin != null) signals.push({ label: 'FCF Margin', score: Math.min(10, fcfMargin * 50), val: pct(fcfMargin) });
  if (ocfGrowth != null) signals.push({ label: 'OCF Growth', score: Math.min(10, 5 + ocfGrowth * 20), val: pct(ocfGrowth) });
  if (revGrowth != null) signals.push({ label: 'Revenue Growth', score: Math.min(10, 5 + revGrowth * 20), val: pct(revGrowth) });
  if (roe != null) signals.push({ label: 'Return on Equity', score: Math.min(10, roe * 25), val: pct(roe) });
  if (debtToEquity != null) signals.push({ label: 'Debt/Equity', score: Math.max(0, 10 - debtToEquity * 3), val: debtToEquity.toFixed(2) + '×' });
  if (accrualsRatio != null) signals.push({ label: 'Earnings Quality', score: Math.max(0, 10 - Math.abs(accrualsRatio) * 100), val: Math.abs(accrualsRatio) < 0.05 ? 'High' : Math.abs(accrualsRatio) < 0.1 ? 'Medium' : 'Low' });

  const avgScore = signals.length ? signals.reduce((s, x) => s + x.score, 0) / signals.length : 5;
  const confidenceScore = Math.round(Math.min(98, Math.max(20, avgScore * 10)));

  // Cash flow trend
  const cfTrend = ocf?.slice(0, 3).map((e, i) => ({ year: e.end.slice(0, 4), val: e.val })).reverse() || [];
  const revTrend = revenue?.slice(0, 3).map((e, i) => ({ year: e.end.slice(0, 4), val: e.val })).reverse() || [];

  // Narrative
  const cashFlowNarrative = ocfNow != null
    ? `Operating cash flow for ${yearNow}: ${fmt(ocfNow)}${ocfGrowth != null ? ` (${ocfGrowth >= 0 ? '+' : ''}${pct(ocfGrowth)} YoY)` : ''}. Free cash flow: ${fmt(fcf)}. ${ocfMargin != null ? `OCF margin ${pct(ocfMargin)} — ${ocfMargin > 0.25 ? 'excellent capital efficiency' : ocfMargin > 0.15 ? 'solid cash generation' : 'room for margin improvement'}.` : ''}`
    : 'Cash flow data unavailable from SEC EDGAR.';

  const earningsNarrative = niNow != null
    ? `Net income ${yearNow}: ${fmt(niNow)}${niGrowth != null ? ` (${niGrowth >= 0 ? '+' : ''}${pct(niGrowth)} YoY)` : ''}. ${epsNow != null ? `Diluted EPS: $${epsNow.toFixed(2)}${epsGrowth != null ? ` (${epsGrowth >= 0 ? '+' : ''}${pct(epsGrowth)} YoY)` : ''}.` : ''} ${accrualsRatio != null ? `Earnings quality score: ${signals.find(s => s.label === 'Earnings Quality')?.val || 'N/A'} — accruals ratio ${(accrualsRatio * 100).toFixed(2)}%${Math.abs(accrualsRatio) < 0.05 ? ' (cash-backed earnings)' : ''}.` : ''}`
    : 'Earnings data unavailable from SEC EDGAR.';

  const risks = [];
  if (debtToEquity != null && debtToEquity > 2) risks.push(`High leverage: D/E ratio ${debtToEquity.toFixed(1)}× — debt servicing pressure if rates rise`);
  if (ocfGrowth != null && ocfGrowth < 0) risks.push(`Declining operating cash flow (${pct(ocfGrowth)} YoY) — monitor closely`);
  if (fcfMargin != null && fcfMargin < 0.05) risks.push(`Thin free cash flow margin (${pct(fcfMargin)}) — limits reinvestment flexibility`);
  if (revGrowth != null && revGrowth < 0.03) risks.push(`Slowing revenue growth (${pct(revGrowth)} YoY) — watch for demand headwinds`);
  if (risks.length === 0) risks.push('No major financial red flags identified from SEC data');
  risks.push('Always cross-reference with latest 10-K risk factors section for company-specific disclosures');

  const opportunities = [];
  if (fcf != null && fcf > 0) opportunities.push(`Strong free cash flow (${fmt(fcf)}) enables buybacks, M&A, or debt paydown`);
  if (revGrowth != null && revGrowth > 0.1) opportunities.push(`Double-digit revenue growth (${pct(revGrowth)}) indicates strong top-line momentum`);
  if (ocfMargin != null && ocfMargin > 0.2) opportunities.push(`High OCF margin (${pct(ocfMargin)}) signals durable competitive advantage`);
  if (roe != null && roe > 0.15) opportunities.push(`ROE of ${pct(roe)} exceeds cost of equity — value-creating management`);
  if (opportunities.length === 0) opportunities.push('Review latest 10-K for growth initiatives and capital allocation guidance');

  return {
    cik: data.cik, companyName, ticker, yearNow, confidenceScore, signals,
    cashFlowNarrative, earningsNarrative, risks, opportunities,
    metrics: {
      ocfNow, fcf, revNow, niNow, debtNow, equityNow, assetsNow, epsNow,
      ocfGrowth, revGrowth, niGrowth, debtToEquity, roe, ocfMargin, fcfMargin,
    },
    trends: { cf: cfTrend, rev: revTrend },
    fmt, pct,
  };
}

function SECModal({ onClose }) {
  const [ticker, setTicker] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [result, setResult] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [error, setError] = useState(null);

  const analyze = async () => {
    const t = ticker.trim().toUpperCase();
    if (!t) { setError('Enter a ticker symbol'); return; }
    setLoading(true); setError(null); setResult(null); setPriceHistory([]);
    try {
      setLoadingStep('Looking up SEC EDGAR…');
      const data = await fetchSECData(t);
      setLoadingStep('Computing financial signals…');
      const analysis = computeAnalysis(data);
      setResult(analysis);
      // Price history is best-effort (server-side); the chart hides itself if empty
      setPriceHistory(Array.isArray(data.prices) ? data.prices : []);
    } catch (e) {
      setError(e.message || 'Could not fetch SEC data. Check the ticker and try again.');
    } finally { setLoading(false); setLoadingStep(''); }
  };

  const scoreColor = (s) => s >= 7 ? '#34d399' : s >= 4 ? '#fbbf24' : '#f87171';
  const pctBar = (s) => `${Math.round(s * 10)}%`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }} />
      <div style={{ position: 'relative', zIndex: 1, background: '#0d0d1a', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 24, padding: 36, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.05)', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#818cf8', fontFamily: 'Space Mono', marginBottom: 8 }}>Finance · AI</div>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#f1f5f9', marginBottom: 6, fontFamily: 'Space Grotesk' }}>SEC Filing Analysis</h2>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24, fontFamily: 'Space Mono' }}>Live data from SEC EDGAR · Any public company</p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          <input value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && !loading && analyze()} placeholder="Ticker: NVDA, AAPL, MSFT, JPM…"
            style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 16px', color: '#f1f5f9', fontFamily: 'Space Mono', fontSize: 14, outline: 'none' }} />
          <button onClick={analyze} disabled={loading} style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', borderRadius: 12, padding: '12px 24px', color: '#fff', fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, whiteSpace: 'nowrap' }}>
            {loading ? loadingStep || 'Loading…' : '→ Analyze'}
          </button>
        </div>
        <p style={{ color: '#334155', fontSize: 11, fontFamily: 'Space Mono', marginBottom: 20 }}>Pulls 10-K filings directly from SEC EDGAR · Not financial advice</p>

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: 14, color: '#f87171', fontSize: 13, marginBottom: 20 }}>{error}</div>}

        {result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', fontFamily: 'Space Grotesk' }}>{result.companyName}</div>
                <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'Space Mono' }}>{result.ticker} · FY{result.yearNow} 10-K · SEC EDGAR</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 40, fontWeight: 700, fontFamily: 'Space Grotesk', background: 'linear-gradient(135deg,#a78bfa,#818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>{result.confidenceScore}</div>
                <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'Space Mono', marginTop: 2 }}>/ 100 signal score</div>
              </div>
            </div>

            {/* Key metrics grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[
                ['Operating CF', result.fmt(result.metrics.ocfNow)],
                ['Free Cash Flow', result.fmt(result.metrics.fcf)],
                ['Revenue', result.fmt(result.metrics.revNow)],
                ['Net Income', result.fmt(result.metrics.niNow)],
                ['Diluted EPS', result.metrics.epsNow != null ? `$${result.metrics.epsNow.toFixed(2)}` : 'N/A'],
                ['Debt / Equity', result.metrics.debtToEquity != null ? `${result.metrics.debtToEquity.toFixed(2)}×` : 'N/A'],
              ].map(([label, val]) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'Space Mono', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', fontFamily: 'Space Grotesk' }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Price chart */}
            {priceHistory.length > 2 && (() => {
              const first = priceHistory[0]?.price;
              const last = priceHistory[priceHistory.length - 1]?.price;
              const change = first ? ((last - first) / first) : 0;
              const isUp = change >= 0;
              const chartColor = isUp ? '#34d399' : '#f87171';
              return (
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono' }}>1-Year Price</div>
                    <div style={{ display: 'flex', align: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', fontFamily: 'Space Grotesk' }}>${last?.toFixed(2)}</span>
                      <span style={{ fontSize: 13, color: chartColor, fontFamily: 'Space Mono', marginLeft: 8 }}>{isUp ? '+' : ''}{(change * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={100}>
                    <AreaChart data={priceHistory} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColor} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#334155', fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} interval={Math.floor(priceHistory.length / 4)} />
                      <YAxis hide domain={['auto', 'auto']} />
                      <Tooltip contentStyle={{ background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 11, fontFamily: 'Space Mono', color: '#f1f5f9' }} formatter={v => [`$${v?.toFixed(2)}`, 'Price']} labelFormatter={l => l} />
                      <Area type="monotone" dataKey="price" stroke={chartColor} strokeWidth={1.5} fill="url(#priceGrad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* Signal bars */}
            {result.signals.length > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 20 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 14 }}>Financial Signals</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {result.signals.map(sig => (
                    <div key={sig.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 130, fontSize: 11, color: '#94a3b8', fontFamily: 'Space Mono', flexShrink: 0 }}>{sig.label}</div>
                      <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 99 }}>
                        <div style={{ height: '100%', width: pctBar(sig.score), background: scoreColor(sig.score), borderRadius: 99, transition: 'width 0.6s ease' }} />
                      </div>
                      <div style={{ width: 60, fontSize: 11, color: scoreColor(sig.score), fontFamily: 'Space Mono', textAlign: 'right', flexShrink: 0 }}>{sig.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analysis */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 14 }}>Analysis</div>
              <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7, marginBottom: 10 }}><strong style={{ color: '#f1f5f9' }}>Cash Flow · </strong>{result.cashFlowNarrative}</p>
              <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7 }}><strong style={{ color: '#f1f5f9' }}>Earnings Quality · </strong>{result.earningsNarrative}</p>
            </div>

            {/* Risks & Opportunities */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#f87171', fontFamily: 'Space Mono', marginBottom: 10 }}>Risk Signals</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {result.risks.map((r, i) => <div key={i} style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5, display: 'flex', gap: 8 }}><span style={{ color: '#f87171', fontSize: 9, marginTop: 4, flexShrink: 0 }}>▲</span>{r}</div>)}
                </div>
              </div>
              <div style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.15)', borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#34d399', fontFamily: 'Space Mono', marginBottom: 10 }}>Strengths</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {result.opportunities.map((o, i) => <div key={i} style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5, display: 'flex', gap: 8 }}><span style={{ color: '#34d399', fontSize: 9, marginTop: 4, flexShrink: 0 }}>◆</span>{o}</div>)}
                </div>
              </div>
            </div>

            {/* EDGAR link */}
            <a href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${result.cik || result.ticker}&type=10-K&dateb=&owner=include&count=10`}
              target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 20px', background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.2)', borderRadius: 12, color: '#818cf8', fontSize: 12, fontFamily: 'Space Mono', textDecoration: 'none' }}>
              View all 10-K filings on SEC EDGAR →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tip Calculator modal ─────────────────────────────────────────────────────
function TipModal({ onClose }) {
  const [bill, setBill] = useState('135.45');
  const [tipPct, setTipPct] = useState(18);
  const [customTip, setCustomTip] = useState('');
  const [split, setSplit] = useState(1);
  const PRESETS = [10, 15, 18, 20, 22, 25, 30];
  const billNum = parseFloat(bill) || 0;
  const tip = billNum * tipPct / 100;
  const total = billNum + tip;
  const fmt = n => '$' + n.toFixed(2);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }} />
      <div style={{ position: 'relative', zIndex: 1, background: '#0d0d1a', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 24, padding: 36, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.05)', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#fbbf24', fontFamily: 'Space Mono', marginBottom: 8 }}>Utility · Finance</div>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#f1f5f9', marginBottom: 24, fontFamily: 'Space Grotesk' }}>Tip Calculator</h2>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 8 }}>Bill Total</div>
          <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '4px 16px' }}>
            <span style={{ fontSize: 28, color: '#64748b', fontFamily: 'Space Grotesk' }}>$</span>
            <input value={bill} onChange={e => setBill(e.target.value)} type="number" min="0" step="0.01"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', fontFamily: 'Space Grotesk', fontSize: 36, fontWeight: 700, padding: '8px 8px' }} />
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono', marginBottom: 10 }}>Tip %</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 10 }}>
            {PRESETS.map(p => (
              <button key={p} onClick={() => { setTipPct(p); setCustomTip(''); }}
                style={{ background: tipPct === p && !customTip ? 'linear-gradient(135deg,#d97706,#f59e0b)' : 'rgba(255,255,255,0.04)', border: `1px solid ${tipPct === p && !customTip ? '#f59e0b' : 'rgba(255,255,255,0.06)'}`, borderRadius: 10, padding: '10px 0', color: tipPct === p && !customTip ? '#07070f' : '#94a3b8', fontFamily: 'Space Mono', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {p}%
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '8px 14px' }}>
            <span style={{ color: '#475569', fontSize: 12, fontFamily: 'Space Mono', whiteSpace: 'nowrap' }}>Custom %</span>
            <input value={customTip} onChange={e => { setCustomTip(e.target.value); if (e.target.value) setTipPct(parseFloat(e.target.value) || 0); }}
              type="number" min="0" max="100" placeholder="e.g. 17"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', fontFamily: 'Space Mono', fontSize: 14 }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, padding: '16px 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono' }}>Split Between</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => setSplit(s => Math.max(1, s - 1))} style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: 'none', color: '#f1f5f9', fontSize: 18, cursor: 'pointer' }}>−</button>
            <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Space Grotesk', minWidth: 32, textAlign: 'center' }}>{split}</span>
            <button onClick={() => setSplit(s => s + 1)} style={{ width: 32, height: 32, borderRadius: '50%', background: '#f59e0b', border: 'none', color: '#07070f', fontSize: 18, cursor: 'pointer', fontWeight: 700 }}>+</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[['Tip Amount', fmt(tip)], ['Total', fmt(total)]].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontFamily: 'Space Mono' }}>{label}</span>
              <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Space Grotesk', color: '#f1f5f9' }}>{val}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 14, padding: '16px 20px', marginTop: 4 }}>
            <span style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#d97706', fontFamily: 'Space Mono' }}>Per Person</span>
            <span style={{ fontSize: 38, fontWeight: 700, fontFamily: 'Space Grotesk', background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{fmt(total / split)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Newsletter section ───────────────────────────────────────────────────────
function Newsletter() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const subscribe = () => {
    if (!email || !email.includes('@')) return;
    window.open(`https://substack.com/profile/shynahegde?utm_source=builditai`, '_blank');
    setSent(true);
  };

  return (
    <section id="newsletter" style={{ background: '#000000', padding: '80px 32px', borderTop: '1px solid #1A1A1A' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#7c3aed', fontFamily: 'Space Mono', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Weekly Newsletter</div>
        <h2 style={{ fontSize: 'clamp(32px,5vw,56px)', fontWeight: 700, letterSpacing: '-0.03em', color: '#FFFFFF', lineHeight: 1.05, marginBottom: 20 }}>
          The Real Intel
        </h2>
        <p style={{ fontSize: 15, color: '#666', lineHeight: 1.7, marginBottom: 8, maxWidth: 440, margin: '0 auto 8px' }}>
          AI insights that cut through the noise — every Friday.
        </p>
        <p style={{ fontSize: 13, color: '#444', marginBottom: 40, fontFamily: 'Space Mono' }}>
          No hype. No fluff. Real signal on what AI means for your business.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 40, flexWrap: 'wrap' }}>
          {['What\'s Working', 'Market Signals', 'Tool Spotlights', 'Strategy Takes'].map(item => (
            <span key={item} style={{ fontSize: 12, color: '#555', fontFamily: 'Space Mono' }}>{item}</span>
          ))}
        </div>

        {sent ? (
          <div style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 12, padding: '16px 24px', color: '#a78bfa', fontFamily: 'Space Grotesk', fontWeight: 600 }}>
            Opening Substack to complete your signup!
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, maxWidth: 460, margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center' }}>
            <input
              value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && subscribe()}
              placeholder="your@email.com"
              style={{ flex: 1, minWidth: 220, background: '#0D0D0D', border: '1px solid #2A2A2A', borderRadius: 99, padding: '13px 20px', color: '#FFFFFF', fontFamily: 'Space Mono', fontSize: 14, outline: 'none' }}
            />
            <button onClick={subscribe}
              style={{ background: '#FFFFFF', border: 'none', borderRadius: 99, padding: '13px 24px', color: '#000000', fontFamily: 'Space Grotesk', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Subscribe →
            </button>
          </div>
        )}
        <p style={{ fontSize: 11, color: '#333', fontFamily: 'Space Mono', marginTop: 16 }}>Every Friday · No spam · Unsubscribe anytime</p>
      </div>
    </section>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  // A shared Value Stream Generator link (?vsd=...) opens the tool directly
  const [activeProject, setActiveProject] = useState(() => (hasValueStreamUrlState() ? PROJECTS.find(p => p.id === 'valuestream') : null));
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Design tokens — Poetic-inspired
  const light = '#FDFCFA';  // warm off-white
  const dark = '#000000';   // pure black
  const ink = '#1A1916';    // near-black text
  const muted = '#6B6B6B';  // muted gray
  const accent = '#6D28D9'; // purple accent (used sparingly)

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Space Grotesk, sans-serif', color: ink, background: light }}>

      {/* ── Nav ──────────────────────────────────────────────────── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'background 0.3s, border-color 0.3s', background: scrolled ? 'rgba(253,252,250,0.92)' : 'transparent', backdropFilter: scrolled ? 'blur(16px)' : 'none', borderBottom: scrolled ? '1px solid #E8E6E1' : '1px solid transparent', padding: '0 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.03em', color: ink }}>BUILDITAI</span>
          <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            {[['#projects','Projects'],['#prompts','Prompts'],['#calculator','Assessment'],['#resources','Resources'],['#newsletter','Newsletter']].map(([href, label]) => (
              <a key={href} href={href} style={{ color: muted, textDecoration: 'none', fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em' }}
                onMouseEnter={e => e.currentTarget.style.color = ink}
                onMouseLeave={e => e.currentTarget.style.color = muted}>{label}</a>
            ))}
            <a href="https://www.linkedin.com/in/shyna/" target="_blank" rel="noopener noreferrer"
              style={{ background: dark, color: light, textDecoration: 'none', fontSize: 13, fontWeight: 600, padding: '9px 20px', borderRadius: 99, letterSpacing: '-0.01em' }}>Connect</a>
          </div>
        </div>
      </nav>

      {/* ── Hero (LIGHT) ──────────────────────────────────────────── */}
      <section style={{ background: light, padding: '160px 32px 80px', borderBottom: '1px solid #E8E6E1' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: accent, fontFamily: 'Space Mono', letterSpacing: '0.1em', textTransform: 'uppercase' }}>AI EXPERIMENTS IN PROGRESS</span>
          </div>
          <h1 style={{ fontSize: 'clamp(52px,8vw,96px)', fontWeight: 700, lineHeight: 1.0, letterSpacing: '-0.04em', color: ink, maxWidth: 820, marginBottom: 40 }}>
            Building real things<br />with AI.
          </h1>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 }}>
            <p style={{ fontSize: 17, color: muted, maxWidth: 440, lineHeight: 1.7, letterSpacing: '-0.01em' }}>
              A collection of AI-powered products, tools, and experiments — from consumer health tech to creative AI.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <a href="#projects" style={{ background: dark, color: light, textDecoration: 'none', fontWeight: 600, padding: '13px 28px', borderRadius: 99, fontSize: 14, letterSpacing: '-0.01em' }}>See Projects →</a>
              <a href="#newsletter" style={{ background: 'transparent', color: ink, textDecoration: 'none', fontWeight: 600, padding: '13px 28px', borderRadius: 99, fontSize: 14, letterSpacing: '-0.01em', border: '1px solid #D0CEC9' }}>Newsletter</a>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 0, marginTop: 80, borderTop: '1px solid #E8E6E1' }}>
            {[['8+', 'AI Projects'], ['20+', 'Years Experience'], ['∞', 'Ideas in Progress']].map(([n, l], i) => (
              <div key={l} style={{ flex: 1, padding: '32px 0', borderRight: i < 2 ? '1px solid #E8E6E1' : 'none', paddingLeft: i > 0 ? 40 : 0 }}>
                <div style={{ fontSize: 48, fontWeight: 700, color: ink, letterSpacing: '-0.04em', lineHeight: 1 }}>{n}</div>
                <div style={{ fontSize: 13, color: muted, fontFamily: 'Space Mono', marginTop: 8, letterSpacing: '0.02em' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Projects (DARK) ───────────────────────────────────────── */}
      <section id="projects" style={{ background: dark, padding: '80px 32px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 56, flexWrap: 'wrap', gap: 16 }}>
            <h2 style={{ fontSize: 'clamp(32px,4vw,52px)', fontWeight: 700, letterSpacing: '-0.03em', color: '#FFFFFF' }}>AI Projects</h2>
            <span style={{ fontSize: 12, color: '#666', fontFamily: 'Space Mono', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Portfolio</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 1, border: '1px solid #1A1A1A' }}>
            {PROJECTS.map(p => <ProjectCard key={p.id} project={p} onClick={setActiveProject} />)}
          </div>
        </div>
      </section>

      {/* ── Prompt Library (LIGHT) ────────────────────────────────── */}
      <PromptLibrary lightBg={light} ink={ink} muted={muted} accent={accent} />

      {/* ── AI Readiness Calculator (DARK) ────────────────────────── */}
      <AIReadinessCalculator darkBg={dark} />

      {/* ── Resource Hub (LIGHT) ──────────────────────────────────── */}
      <ResourceHub lightBg={light} ink={ink} muted={muted} accent={accent} />

      {/* ── Newsletter (DARK) ─────────────────────────────────────── */}
      <Newsletter darkBg={dark} />

      {/* ── About (LIGHT) ─────────────────────────────────────────── */}
      <section id="about" style={{ background: light, padding: '80px 32px 80px', borderTop: '1px solid #E8E6E1' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: 11, color: accent, fontFamily: 'Space Mono', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 20 }}>Builder</div>
              <h2 style={{ fontSize: 'clamp(28px,3vw,40px)', fontWeight: 500, letterSpacing: '-0.02em', color: muted, lineHeight: 1.1, marginBottom: 28 }}>Shyna Hegde</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['AI Products', 'Health Tech', 'Agentic AI', 'EdTech', 'Creative Tech'].map(tag => (
                  <span key={tag} style={{ fontSize: 11, padding: '5px 12px', borderRadius: 99, background: 'transparent', color: muted, border: '1px solid #D0CEC9', fontFamily: 'Space Mono' }}>{tag}</span>
                ))}
              </div>
            </div>
            <div>
              <p style={{ color: muted, lineHeight: 1.8, fontSize: 16, marginBottom: 20, letterSpacing: '-0.01em' }}>
                Product builder and AI enthusiast with 20+ years of experience turning complex problems into things people actually use. I love the intersection of technology, design, and real-world impact.
              </p>
              <p style={{ color: muted, lineHeight: 1.8, fontSize: 16, marginBottom: 32, letterSpacing: '-0.01em' }}>
                BuildItAI is my personal lab: shipping real products, exploring what's possible, and having fun building with AI.
              </p>
              <a href="https://www.linkedin.com/in/shyna/" target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', background: dark, color: light, textDecoration: 'none', fontSize: 14, fontWeight: 600, padding: '12px 28px', borderRadius: 99 }}>View LinkedIn →</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer (DARK) ─────────────────────────────────────────── */}
      <footer style={{ background: dark, padding: '40px 32px', borderTop: '1px solid #1A1A1A' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.03em', color: '#FFFFFF' }}>BUILDITAI</span>
          <p style={{ color: '#555', fontSize: 13, fontFamily: 'Space Mono' }}>© 2026 Shyna Hegde · <a href="https://www.linkedin.com/in/shyna/" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none' }}>LinkedIn</a></p>
        </div>
      </footer>

      {/* Modals */}
      {activeProject?.id === 'sec' && <SECModal onClose={() => setActiveProject(null)} />}
      {activeProject?.id === 'tipcalc' && <TipModal onClose={() => setActiveProject(null)} />}
      {activeProject?.id === 'valuestream' && <ValueStreamModal onClose={() => setActiveProject(null)} />}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #FDFCFA; }
        ::-webkit-scrollbar-thumb { background: #D0CEC9; border-radius: 99px; }
        input::placeholder, textarea::placeholder { color: #999; }
        @media (max-width: 768px) {
          nav > div > div:last-child a:not(:last-child) { display: none; }
          #about > div > div { grid-template-columns: 1fr !important; gap: 40px !important; }
          #resources > div > div:last-child { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          #projects > div > div[style*="minmax(340px"] { grid-template-columns: 1fr !important; }
          #prompts > div > div[style*="minmax(380px"] { grid-template-columns: 1fr !important; }
          #calculator > div > div > div[style*="48px"] { padding: 28px 20px !important; }
          section { padding-left: 16px !important; padding-right: 16px !important; }
        }
      `}</style>
    </div>
  );
}
